import React, { useState, useEffect, useMemo, useRef } from 'react';
import SignalPanel from './SignalPanel';
import './ContractTrading.css';
import { usePrices } from '../contexts/PriceContext';

const ContractTrading = ({ executedTrades = [], addTrade, updateTrade, closeTrade }) => {
    // Get prices and live balance from PriceContext (balance pushed via WebSocket)
    const { prices, isConnected, connectionStatus, balance: wsBalance } = usePrices();
    
    // Use refs to access current values in useEffect without causing re-renders
    const executedTradesRef = useRef(executedTrades);
    const pricesRef = useRef(prices);
    const updateTradeRef = useRef(updateTrade);
    
    executedTradesRef.current = executedTrades;
    pricesRef.current = prices;
    updateTradeRef.current = updateTrade;
    
    // Core State
    const [isRunning, setIsRunning] = useState(true);
    // Simulation mode: true = paper trades (no real orders sent), false = live orders
    const [isSimulation, setIsSimulation] = useState(() => {
        const saved = localStorage.getItem('bot_simulation_mode');
        return saved !== null ? JSON.parse(saved) : true; // default ON (safe)
    });
    // Persist simulation mode preference
    useEffect(() => {
        localStorage.setItem('bot_simulation_mode', JSON.stringify(isSimulation));
    }, [isSimulation]);
    const [bybitConnected, setBybitConnected] = useState(false);
    const [bybitConnectionStatus, setBybitConnectionStatus] = useState('Not connected');
    // Use live balance from WebSocket (wsBalance) — fallback to 0 until first push arrives
    // Server broadcasts: { totalEquity, availableBalance, unrealisedPnl, accountType }
    const balance = wsBalance ? (wsBalance.availableBalance ?? wsBalance.totalEquity ?? 0) : 0;
    const [lastMarketUpdate, setLastMarketUpdate] = useState('11:22:21 AM');
    const [lastPriceUpdate, setLastPriceUpdate] = useState(new Date().toLocaleTimeString());
    const [autoUpdateStatus, setAutoUpdateStatus] = useState('Setting up...');
    const [wsConnected, setWsConnected] = useState(true);
    const [activeTradesCount, setActiveTradesCount] = useState(0);
    const [symbolsLoaded, setSymbolsLoaded] = useState(142);
    // Guards against duplicate order submission (double-click, rapid re-click, etc.)
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);

    // Dynamic pairs loaded from server (all Bybit symbols)
    const [availablePairs, setAvailablePairs] = useState([]);
    const [pairSearch, setPairSearch] = useState('');
    useEffect(() => {
        fetch('/api/bybit/symbols')
            .then(r => r.json())
            .then(d => {
                if (d.success && d.symbols.length > 0) {
                    // Strip USDT suffix for display — store full symbol
                    setAvailablePairs(d.symbols);
                    setSymbolsLoaded(d.symbols.length);
                    console.log(`📋 Loaded ${d.symbols.length} Bybit symbols`);
                }
            })
            .catch(() => {});
    }, []);

    // Sync connection status from PriceContext (which manages the WebSocket)
    useEffect(() => {
        setBybitConnected(isConnected);
        setBybitConnectionStatus(isConnected ? 'Connected to Bybit Live' : connectionStatus);
    }, [isConnected, connectionStatus]);
    
    // Sync positions from Bybit to update entry prices - CRITICAL: Always sync if there are active trades
    useEffect(() => {
        let syncFailureCount = 0;
        const MAX_SYNC_FAILURES = 5;
        
        const syncPositionsFromBybit = async () => {
            // Always sync if there are active trades (regardless of connection status)
            const activeTrades = executedTradesRef.current.filter(t => t.status === 'ACTIVE');
            if (activeTrades.length === 0) {
                syncFailureCount = 0; // Reset failure count when no active trades
                return; // No active trades to sync
            }
            
            console.log(`🔄 Starting sync for ${activeTrades.length} active trade(s)...`);
            
            try {
                // Add timeout to fetch request (8 seconds)
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 8000);
                
                const response = await fetch('/api/bybit/positions', {
                    signal: controller.signal,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                
                clearTimeout(timeoutId);
                
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                
                const data = await response.json();
                
                // Reset failure count on success
                syncFailureCount = 0;
                
                if (data.success && data.positions) {
                    console.log('🔄 ========== SYNCING POSITIONS FROM BYBIT ==========');
                    console.log(`📊 Found ${data.positions.length} positions on Bybit`);
                    console.log(`📊 Found ${executedTradesRef.current.length} trades in local state`);
                    
                    // Log all Bybit positions
                    data.positions.forEach((pos, idx) => {
                        console.log(`  Position ${idx + 1}: ${pos.symbol} ${pos.side} - Entry: ${pos.avgPrice || pos.entryPrice}, Mark: ${pos.markPrice}`);
                    });
                    
                    // Log all local trades
                    executedTradesRef.current.forEach((trade, idx) => {
                        const tradeSymbol = trade.pair.endsWith('USDT') ? trade.pair : trade.pair + 'USDT';
                        const tradeSide = trade.action === 'BUY' ? 'Buy' : 'Sell';
                        console.log(`  Trade ${idx + 1}: ${tradeSymbol} ${tradeSide} - Entry: ${trade.entryPrice}, Current: ${trade.currentPrice}`);
                    });
                    
                    // Update trades with actual Bybit position data
                    data.positions.forEach(bybitPos => {
                        console.log(`\n🔍 Checking Bybit position: ${bybitPos.symbol} ${bybitPos.side}`);
                        
                        // Find matching trade by symbol and side
                        // Handle various action formats: 'BUY', 'SELL', 'Buy', 'Sell', 'buy', 'sell'
                        const matchingTrade = executedTradesRef.current.find(trade => {
                            const tradeSymbol = trade.pair.endsWith('USDT') ? trade.pair : trade.pair + 'USDT';
                            
                            // Normalize trade side to match Bybit's format ('Buy' or 'Sell')
                            let tradeSide = 'Sell'; // Default
                            const actionUpper = (trade.action || '').toUpperCase();
                            if (actionUpper === 'BUY') {
                                tradeSide = 'Buy';
                            } else if (actionUpper === 'SELL' || actionUpper === 'SHORT') {
                                tradeSide = 'Sell';
                            }
                            
                            console.log(`  Comparing: Trade(${tradeSymbol} ${tradeSide} [from action: ${trade.action}]) vs Bybit(${bybitPos.symbol} ${bybitPos.side})`);
                            
                            const matches = tradeSymbol === bybitPos.symbol && tradeSide === bybitPos.side;
                            if (matches) {
                                console.log(`  ✅ MATCH FOUND!`);
                            }
                            
                            return matches;
                        });
                        
                        if (matchingTrade) {
                            const bybitEntryPrice = parseFloat(bybitPos.avgPrice || bybitPos.entryPrice || 0);
                            const bybitMarkPrice = parseFloat(bybitPos.markPrice || 0);
                            const bybitUnrealisedPnl = parseFloat(bybitPos.unrealisedPnl || bybitPos.unrealizedPnL || 0);
                            const bybitPnlPercent = parseFloat(bybitPos.pnlPercent || 0);
                            
                            const currentEntryPrice = parseFloat(matchingTrade.entryPrice || 0);
                            const currentDisplayPrice = parseFloat(matchingTrade.currentPrice || 0);
                            const priceDiff = bybitEntryPrice > 0 ? Math.abs(bybitEntryPrice - currentEntryPrice) : 0;
                            const currentPriceDiff = bybitMarkPrice > 0 ? Math.abs(bybitMarkPrice - currentDisplayPrice) : 0;
                            
                            console.log(`  📊 Price comparison:`);
                            console.log(`     Entry Price - Bot: ${currentEntryPrice}, Bybit: ${bybitEntryPrice}, Diff: ${priceDiff}`);
                            console.log(`     Current Price - Bot: ${currentDisplayPrice}, Bybit MarkPrice: ${bybitMarkPrice}, Diff: ${currentPriceDiff}`);
                            console.log(`     Bybit P&L: ${bybitUnrealisedPnl} (${bybitPnlPercent}%)`);
                            
                            // Always update if there's ANY difference in entry price (> 0.0001) or if needsSync flag is set
                            const needsEntryPriceUpdate = matchingTrade.needsSync || 
                                                          currentEntryPrice === 0 || 
                                                          !currentEntryPrice ||
                                                          (bybitEntryPrice > 0 && priceDiff > 0.0001);
                            
                            // ALWAYS update current price if markPrice is different (even by 0.01)
                            const needsCurrentPriceUpdate = bybitMarkPrice > 0 && (
                                currentDisplayPrice === 0 ||
                                !currentDisplayPrice ||
                                currentPriceDiff > 0.01 // Update if difference > 0.01
                            );
                            
                            console.log(`  🔄 Update flags:`);
                            console.log(`     - needsEntryPriceUpdate: ${needsEntryPriceUpdate}`);
                            console.log(`     - needsCurrentPriceUpdate: ${needsCurrentPriceUpdate} (diff: ${currentPriceDiff})`);
                            
                            // ALWAYS update ALL values from Bybit (source of truth) - no exceptions
                            const updateData = {
                                entryPrice: bybitEntryPrice, // ALWAYS use Bybit's entry price
                                currentPrice: bybitMarkPrice, // ALWAYS use Bybit's mark price (THIS IS THE MARKET PRICE)
                                profitLoss: bybitUnrealisedPnl, // ALWAYS use Bybit's P&L
                                profitPercent: bybitPnlPercent, // ALWAYS use Bybit's P&L %
                                bybitPosition: bybitPos,
                                needsSync: false, // Clear the flag
                                _lastSync: Date.now() // Force React update
                            };
                            
                            console.log(`  ✅ UPDATING trade with Bybit data (FORCE UPDATE):`, {
                                entryPrice: `${currentEntryPrice} → ${bybitEntryPrice}`,
                                currentPrice: `${currentDisplayPrice} → ${bybitMarkPrice} (Bybit MarkPrice = Market Price)`,
                                profitLoss: `${matchingTrade.profitLoss} → ${bybitUnrealisedPnl}`,
                                profitPercent: `${matchingTrade.profitPercent} → ${bybitPnlPercent}`
                            });
                            
                            // Call update function
                            if (updateTradeRef.current) {
                                updateTradeRef.current(matchingTrade.id, updateData);
                                console.log(`  ✅ Update function called for trade ID: ${matchingTrade.id}`);
                                console.log(`  ✅ Current price should now be: ${bybitMarkPrice} (matching Bybit's markPrice)`);
                            } else {
                                console.error(`  ❌ updateTradeRef.current is null! Cannot update trade.`);
                            }
                            
                            console.log(`  ✅ Update complete!`);
                        } else {
                            console.log(`  ❌ No matching trade found for ${bybitPos.symbol} ${bybitPos.side}`);
                            console.log(`     Looking for: symbol=${bybitPos.symbol}, side=${bybitPos.side}`);
                            console.log(`     Available trades:`);
                            executedTradesRef.current.forEach((trade, idx) => {
                                const tradeSymbol = trade.pair.endsWith('USDT') ? trade.pair : trade.pair + 'USDT';
                                const tradeSide = (trade.action || '').toUpperCase() === 'BUY' ? 'Buy' : 'Sell';
                                console.log(`       ${idx + 1}. ${tradeSymbol} ${tradeSide} (action: ${trade.action})`);
                            });
                        }
                    });
                    
                    console.log('🔄 ================================================\n');
                } else {
                    console.warn('⚠️  Position sync failed or no positions:', data);
                    syncFailureCount++;
                }
            } catch (error) {
                syncFailureCount++;
                
                if (error.name === 'AbortError') {
                    console.error('❌ Position sync timeout after 8 seconds');
                } else {
                    console.error('❌ Error syncing positions from Bybit:', error);
                }
                
                // If we have too many consecutive failures, log a warning
                if (syncFailureCount >= MAX_SYNC_FAILURES) {
                    console.error(`❌ Position sync has failed ${syncFailureCount} times consecutively. Check Bybit connection.`);
                    // Don't throw - allow sync to continue trying
                }
            }
        };
        
        // CRITICAL: Always sync immediately if there are active trades (don't wait for connection status)
        syncPositionsFromBybit();
        
        // Sync every 1 second for real-time accuracy (critical for trading)
        const interval = setInterval(() => {
            syncPositionsFromBybit();
        }, 1000);
        
        return () => clearInterval(interval);
    }, [executedTrades.length]); // Re-run when number of trades changes

    // Manual Trading State
    const [manualTrade, setManualTrade] = useState({
        pair: 'BTCUSDT',
        amount: 100,
        leverage: 10,
        action: 'BUY'
    });

    // Automated Trading State - Load from localStorage
    const [automatedConfig, setAutomatedConfig] = useState(() => {
        const saved = localStorage.getItem('automatedConfig');
        return saved ? JSON.parse(saved) : {
            enabled: false,
            riskLevel: 'conservative',
            maxTrades: 5,
            takeProfit: 5,
            stopLoss: 2
        };
    });

    // Save automated config to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('automatedConfig', JSON.stringify(automatedConfig));
    }, [automatedConfig]);

    // Statistics
    const [stats, setStats] = useState({
        totalTrades: 0,
        winningTrades: 0,
        totalProfit: 0,
        dailyProfit: 0,
        successRate: 0
    });

    // Complete list of trading pairs (no duplicates)
    const tradingPairs = [
        'ADA', 'ALGO', 'APT', 'ARB', 'ATOM', 'AUD', 'AVAX', 'BNB', 'BCH', 'BUSD',
        'BTC', 'CAD', 'CHF', 'COPPER', 'DAI', 'DOT', 'DOGE', 'ETC', 'ETH', 'EUR',
        'FIL', 'FTM', 'GAS', 'GBP', 'JPY', 'LINK', 'LTC', 'MATIC', 'NEAR', 'NICKEL',
        'NZD', 'OIL', 'PALLADIUM', 'PLATINUM', 'SHIB', 'SOL', 'TRX', 'UNI', 'USDC',
        'USDT', 'VET', 'XAG', 'XAU', 'XLM', 'XPD', 'XRP'
    ];

    // Risk levels
    const riskLevels = [
        { name: '90% Conservative', value: 'conservative' },
        { name: '70% Balanced', value: 'balanced' },
        { name: '50% Aggressive', value: 'aggressive' },
        { name: '30% High Risk', value: 'high-risk' }
    ];

    // Market data is now provided by PriceContext
    // Signal recommendations are handled by <SignalPanel> which polls /api/analysis every 60s

    // Execute Manual Trade
    const executeManualTrade = async () => {
        // Guard: prevent duplicate order submission from double-clicks or rapid re-clicks
        if (isPlacingOrder) {
            console.warn('⚠️  Order already in progress — ignoring duplicate click');
            return;
        }
        setIsPlacingOrder(true);
        try {
            await executeManualTradeInner();
        } finally {
            setIsPlacingOrder(false);
        }
    };

    const executeManualTradeInner = async () => {
        // Prepare symbol for Bybit (ensure it ends with USDT)
        const symbol = manualTrade.pair.endsWith('USDT') ? manualTrade.pair : manualTrade.pair + 'USDT';
        // Display pair name (strip USDT suffix for readability)
        const displayPair = symbol.replace(/USDT$/, '');

        // ── Resolve live entry price ──────────────────────────────────────────
        // prices[] values are {price, bid, ask} objects from PriceContext
        const resolvePrice = (key) => {
            const v = prices[key];
            if (!v) return 0;
            const p = typeof v === 'object' ? (v.price || v.lastPrice || 0) : v;
            return parseFloat(p) || 0;
        };
        let entryPrice = resolvePrice(symbol) || resolvePrice(manualTrade.pair);

        // If PriceContext hasn't received the WS snapshot yet, ask the server directly
        if (!entryPrice) {
            try {
                const pr = await fetch(`/api/bybit/price/${symbol}`).then(r => r.json());
                entryPrice = parseFloat(pr.price || pr.lastPrice || 0);
            } catch (e) {}
        }

        // Map action to Bybit side
        const side = manualTrade.action === 'BUY' ? 'Buy' : 'Sell';

        // ── SIMULATION MODE: paper trade, no real order ─────────────────────
        if (isSimulation) {
            if (!entryPrice) {
                alert(`⚠️ Cannot open trade: no live price available for ${displayPair} yet.\nWait a moment for the price feed to connect, then try again.`);
                return;
            }
            const simId = `SIM_${Date.now()}`;
            const trade = {
                id: simId,
                pair: displayPair,
                action: manualTrade.action,
                amount: manualTrade.amount,
                leverage: manualTrade.leverage,
                entryPrice,
                currentPrice: entryPrice,
                timestamp: new Date().toISOString(),
                type: 'CONTRACT_TRADING',
                status: 'ACTIVE',
                profitLoss: 0,
                profitPercent: 0,
                isSimulation: true,
            };
            try {
                await fetch('/api/trades', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(trade)
                });
            } catch (e) {}
            addTrade(trade);
            setActiveTradesCount(prev => prev + 1);
            setManualTrade({ pair: 'BTCUSDT', amount: 100, leverage: 10, action: 'BUY' });
            setPairSearch('');
            alert(`📊 [SIMULATION] Paper trade opened!\n${manualTrade.action} ${displayPair} @ $${entryPrice.toFixed(4)}\nNo real money used.`);
            return;
        }

        try {
            console.log(`📤 Placing order on Bybit: ${side} $${manualTrade.amount} of ${symbol} @ ${entryPrice}`);

            // Place order on Bybit — server calculates qty using instrument rules
            const response = await fetch('/api/bybit/order', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    symbol: symbol,
                    side: side,
                    amountUSDT: manualTrade.amount,
                    leverage: manualTrade.leverage || 10,
                    orderType: 'Market'
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                console.log('✅ Order placed on Bybit:', result.orderId);
                console.log('📥 Full order response:', result);
                
                // CRITICAL: Only use Bybit's actual entry price - NEVER use local price feed
                if (!result.actualEntryPrice) {
                    console.error('❌ WARNING: No actual entry price from Bybit!');
                    console.error('   This means the position was not found immediately.');
                    console.error('   The trade will be created with a placeholder price and synced shortly.');
                    console.error('   Local price feed (', entryPrice, ') is NOT accurate and will NOT be used.');
                }
                
                // Use Bybit's actual entry price, or null if not available (will be synced)
                const actualEntryPrice = result.actualEntryPrice || null;
                console.log(`📊 Entry price: Bybit actual=${result.actualEntryPrice || 'NOT YET AVAILABLE'}, local feed=${entryPrice} (IGNORED)`);
                
                // If we don't have the actual entry price yet, we'll create the trade with a temporary value
                // and the position sync will update it shortly
                const tempEntryPrice = actualEntryPrice || 0; // Temporary - will be updated by sync
                
                // Create local trade record
                const trade = {
                    id: result.orderId || Date.now().toString(),
                    pair: displayPair,
                    action: manualTrade.action,
                    amount: manualTrade.amount,
                    leverage: manualTrade.leverage,
                    entryPrice: actualEntryPrice || tempEntryPrice, // Use Bybit's actual entry price or temp
                    currentPrice: result.actualPosition?.markPrice || actualEntryPrice || tempEntryPrice, // Use Bybit's mark price
                    timestamp: new Date().toISOString(),
                    type: 'CONTRACT_TRADING',
                    status: 'ACTIVE',
                    profitLoss: 0,
                    profitPercent: 0,
                    bybitOrderId: result.orderId,
                    bybitPosition: result.actualPosition, // Store full position data
                    needsSync: !actualEntryPrice, // Flag to indicate this needs position sync
                    signal: manualTrade._appliedSignal || null // Signal that triggered this trade (from Signal Panel)
                };
                
                console.log('🔍 Created trade:', trade);
                if (!actualEntryPrice) {
                    console.warn('⚠️  Trade created with temporary entry price - will be updated by position sync');
                }
                
                // Persist new trade to server
                try {
                    await fetch('/api/trades', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(trade)
                    });
                } catch (e) { console.warn('⚠️  Could not persist trade to server:', e.message); }

                // Add trade to local state
                addTrade(trade);
                setActiveTradesCount(prev => prev + 1);

                if (actualEntryPrice) {
                    alert(`✅ Trade executed on Bybit! Order ID: ${result.orderId}\nEntry Price: ${actualEntryPrice}`);
                } else {
                    alert(`✅ Trade executed on Bybit! Order ID: ${result.orderId}\nEntry price will be synced shortly...`);
                }
            } else {
                console.error('❌ Failed to place order on Bybit:', result.error);
                alert(`❌ Failed to execute trade: ${result.error}`);
                return;
            }
        } catch (error) {
            console.error('❌ Error executing trade on Bybit:', error);
            alert(`❌ Error executing trade: ${error.message}`);
            return;
        }
        
        // Reset form
        setManualTrade({
            pair: 'BTCUSDT',
            amount: 100,
            leverage: 10,
            action: 'BUY'
        });
        setPairSearch('');
    };

    // Execute Alert Trade
    const executeAlertTrade = async (alert) => {
        // Guard: prevent duplicate order submission from rapid re-clicks
        if (isPlacingOrder) {
            console.warn('⚠️  Order already in progress — ignoring duplicate alert trade click');
            return;
        }
        setIsPlacingOrder(true);
        try {
            await executeAlertTradeInner(alert);
        } finally {
            setIsPlacingOrder(false);
        }
    };

    const executeAlertTradeInner = async (alert) => {
        // Get normalized price from current market data instead of alert price
        let entryPrice = null;
        const possibleKeys = [
            alert.pair,                    // Direct match (BTC, ETH, etc.)
            alert.pair + 'USDT',           // BTCUSDT, ETHUSDT, etc.
            alert.pair + 'USD'             // BTCUSD, ETHUSD, etc.
        ];
        
        for (const key of possibleKeys) {
            if (prices[key]?.price) {
                entryPrice = prices[key].price;
                console.log(`✅ Found normalized price for alert trade ${alert.pair} (using ${key}):`, entryPrice);
                break;
            }
        }
        
        // Fallback to reasonable price if no real price found
        if (!entryPrice) {
            const fallbackPrices = {
                'BTC': 50000,
                'ETH': 3000,
                'SOL': 100,
                'ADA': 0.5,
                'MATIC': 1.0,
                'BNB': 300,
                'XRP': 0.6,
                'DOGE': 0.1,
                'LTC': 80,
                'LINK': 15,
                'AVAX': 25,
                'ATOM': 10,
                'UNI': 8
            };
            entryPrice = fallbackPrices[alert.pair] || 100;
            console.log(`⚠️ No real price found for alert ${alert.pair}, using fallback:`, entryPrice);
        }
        
        // Prepare symbol for Bybit
        const symbol = alert.pair.endsWith('USDT') ? alert.pair : alert.pair + 'USDT';
        const amount = 100; // Default amount for alert trades
        
        // Calculate quantity (amount in USDT / entryPrice = number of contracts)
        let qty = amount / entryPrice;
        
        // Ensure minimum quantity of 0.001
        if (qty < 0.001) {
            qty = 0.001;
            console.warn(`⚠️  Calculated quantity too small, using minimum 0.001`);
        }
        
        // Round to 3 decimal places
        qty = Math.floor(qty * 1000) / 1000;
        const qtyString = qty.toFixed(3);
        
        console.log(`📊 Alert trade quantity: amount=${amount}, price=${entryPrice}, qty=${qtyString}`);
        
        const side = alert.signal === 'BUY' ? 'Buy' : 'Sell';
        
        try {
            console.log(`📤 Placing alert trade on Bybit: ${side} ${qty} ${symbol}`);
            
            // Place order on Bybit
            const response = await fetch('/api/bybit/order', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    symbol: symbol,
                    side: side,
                    qty: qtyString,
                    orderType: 'Market'
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                console.log('✅ Alert trade placed on Bybit:', result.orderId);
                console.log('📥 Full alert order response:', result);
                
                // CRITICAL: Only use Bybit's actual entry price - NEVER use local price feed
                if (!result.actualEntryPrice) {
                    console.error('❌ WARNING: No actual entry price from Bybit for alert trade!');
                    console.error('   The trade will be created with a placeholder price and synced shortly.');
                }
                
                // Use Bybit's actual entry price, or null if not available (will be synced)
                const actualEntryPrice = result.actualEntryPrice || null;
                console.log(`📊 Alert entry price: Bybit actual=${result.actualEntryPrice || 'NOT YET AVAILABLE'}, local feed=${entryPrice} (IGNORED)`);
                
                // If we don't have the actual entry price yet, we'll create the trade with a temporary value
                const tempEntryPrice = actualEntryPrice || 0; // Temporary - will be updated by sync
                
                const trade = {
                    id: result.orderId || Date.now().toString(),
                    pair: alert.pair,
                    action: alert.signal,
                    amount: amount,
                    leverage: 10,
                    entryPrice: actualEntryPrice || tempEntryPrice, // Use Bybit's actual entry price or temp
                    currentPrice: result.actualPosition?.markPrice || actualEntryPrice || tempEntryPrice, // Use Bybit's mark price
                    timestamp: new Date().toISOString(),
                    type: 'CONTRACT_TRADING',
                    status: 'ACTIVE',
                    profitLoss: 0,
                    profitPercent: 0,
                    bybitOrderId: result.orderId,
                    bybitPosition: result.actualPosition, // Store full position data
                    needsSync: !actualEntryPrice // Flag to indicate this needs position sync
                };
                
                addTrade(trade);
                setActiveTradesCount(prev => prev + 1);
                
                if (actualEntryPrice) {
                    alert(`✅ Alert trade executed on Bybit! Order ID: ${result.orderId}\nEntry Price: ${actualEntryPrice}`);
                } else {
                    alert(`✅ Alert trade executed on Bybit! Order ID: ${result.orderId}\nEntry price will be synced shortly...`);
                }
            } else {
                console.error('❌ Failed to place alert trade on Bybit:', result.error);
                alert(`❌ Failed to execute alert trade: ${result.error}`);
            }
        } catch (error) {
            console.error('❌ Error executing alert trade on Bybit:', error);
            alert(`❌ Error executing alert trade: ${error.message}`);
        }
    };

    // Close Trade - Actually close on Bybit
    const handleCloseTrade = async (tradeId) => {
        const trade = executedTrades.find(t => t.id === tradeId);
        if (!trade) {
            alert('❌ Trade not found');
            return;
        }
        
        // Get symbol and side for Bybit
        const symbol = trade.pair.endsWith('USDT') ? trade.pair : trade.pair + 'USDT';
        const side = trade.action === 'BUY' ? 'Buy' : 'Sell';
        
        // Get quantity — send amountUSDT to server and let it calculate+round correctly
        // (same approach as opening: server knows the qtyStep for every symbol)
        let qty = null;
        let amountUSDT = null;

        if (trade.bybitPosition && parseFloat(trade.bybitPosition.size || 0) > 0) {
            // Real trade: Bybit already knows the exact position size
            qty = parseFloat(trade.bybitPosition.size).toString();
        } else {
            // Sim trade or no position data: send USDT amount, server calculates qty
            amountUSDT = trade.amount;
        }

        // ── SIMULATION MODE close ────────────────────────────────────────────
        // ONLY close as simulation if the trade itself was opened as a simulation.
        // The current isSimulation toggle does NOT affect closing of already-open real trades.
        if (trade.isSimulation === true) {
            const exitPrice = parseFloat(trade.currentPrice || trade.entryPrice || 0);
            const entryPrice = parseFloat(trade.entryPrice || 0);
            let finalPnl = 0;
            if (entryPrice > 0 && exitPrice > 0) {
                const priceDiff = trade.action === 'BUY' ? exitPrice - entryPrice : entryPrice - exitPrice;
                finalPnl = (priceDiff / entryPrice) * (trade.leverage || 1) * (trade.amount || 0);
            }
            finalPnl = isFinite(finalPnl) ? finalPnl : 0;
            const closedTrade = { ...trade, status: 'CLOSED', closedAt: new Date().toISOString(), exitPrice, profitLoss: finalPnl };
            try {
                await fetch(`/api/trades/${tradeId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(closedTrade)
                });
            } catch (e) {}
            closeTrade(tradeId);
            setActiveTradesCount(prev => Math.max(0, prev - 1));
            alert(`📊 [SIMULATION] Paper trade closed!\nP&L: ${finalPnl >= 0 ? '+' : ''}$${finalPnl.toFixed(2)}\nNo real money used.`);
            return;
        }

        if (!window.confirm(`Close ${trade.pair} ${trade.action} position?`)) {
            return;
        }

        try {
            console.log(`🔴 Closing position on Bybit: ${symbol} ${side} qty=${qty} amountUSDT=${amountUSDT}`);

            const response = await fetch('/api/bybit/close-position', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ symbol, side, qty, amountUSDT })
            });
            
            const result = await response.json();
            
            if (result.success) {
                // Calculate final P&L
                const exitPrice = result.exitPrice || parseFloat(trade.currentPrice || trade.entryPrice || 0);
                const entryPrice = parseFloat(trade.bybitPosition?.avgPrice || trade.entryPrice || 0);
                let finalPnl = 0;
                if (entryPrice > 0 && exitPrice > 0) {
                    const priceDiff = trade.action === 'BUY' ? exitPrice - entryPrice : entryPrice - exitPrice;
                    finalPnl = (priceDiff / entryPrice) * parseFloat(trade.leverage || 1) * parseFloat(trade.amount || 0);
                }
                finalPnl = isFinite(finalPnl) ? finalPnl : 0;

                // Persist closed trade to server
                const closedTrade = {
                    ...trade,
                    status: 'CLOSED',
                    closedAt: new Date().toISOString(),
                    exitPrice,
                    profitLoss: finalPnl,
                    profitPercent: entryPrice > 0 ? ((exitPrice - entryPrice) / entryPrice * 100 * (trade.leverage || 1)) : 0,
                };
                try {
                    await fetch(`/api/trades/${tradeId}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(closedTrade)
                    });
                } catch (e) { console.warn('⚠️  Could not persist closed trade:', e.message); }

                // Always remove from UI on success
                closeTrade(tradeId);
                setActiveTradesCount(prev => Math.max(0, prev - 1));
                const msg = result.message?.includes('already closed')
                    ? `✅ Removed from dashboard.\n(Position was already closed on Bybit)`
                    : `✅ Position closed!\nP&L: ${finalPnl >= 0 ? '+' : ''}$${finalPnl.toFixed(2)}\nOrder ID: ${result.orderId || result.orderLinkId}`;
                alert(msg);
            } else {
                console.error('❌ Failed to close position:', result.error);
                alert(`❌ Failed to close position: ${result.error}`);
            }
        } catch (error) {
            console.error('❌ Error closing position:', error);
            alert(`❌ Error closing position: ${error.message}`);
        }
    };

    // Update Trade Leverage
    const updateTradeLeverage = (tradeId, newLeverage) => {
        const trade = executedTrades.find(t => t.id === tradeId);
        if (trade) {
            const updatedTrade = { ...trade, leverage: parseInt(newLeverage) };
            // Recalculate P&L with new leverage
            const { profitPercent, profitLoss } = calculateTradePnL(updatedTrade);
            updateTrade(tradeId, {
                leverage: parseInt(newLeverage),
                profitPercent,
                profitLoss
            });
        }
    };

    // Calculate Trade P&L — always uses the most accurate source available
    const calculateTradePnL = (trade) => {
        // Real trade: Bybit is the source of truth
        if (trade.bybitPosition) {
            const bybitPnl = parseFloat(trade.bybitPosition.unrealisedPnl || trade.bybitPosition.unrealizedPnL || trade.profitLoss || 0);
            const bybitPnlPercent = parseFloat(trade.bybitPosition.pnlPercent || trade.profitPercent || 0);
            return { profitPercent: bybitPnlPercent, profitLoss: bybitPnl };
        }

        // Simulation trade: use live price from context for current price
        const entry = parseFloat(trade.entryPrice || 0);
        if (!entry) return { profitPercent: 0, profitLoss: 0 };

        // Prefer live price from context; fall back to stored currentPrice
        const livePrice = getLivePrice(trade);
        const current = livePrice || parseFloat(trade.currentPrice || 0);
        if (!current) return { profitPercent: 0, profitLoss: 0 };

        const priceChange = trade.action === 'BUY'
            ? (current - entry) / entry
            : (entry - current) / entry;

        return {
            profitPercent: priceChange * (trade.leverage || 1) * 100,
            profitLoss:    priceChange * (trade.leverage || 1) * (trade.amount || 0),
        };
    };

    // Helper: get live price for a trade from the prices context (plain number)
    const getLivePrice = (trade) => {
        const symbol = trade.pair.endsWith('USDT') ? trade.pair : trade.pair + 'USDT';
        const keys = [symbol, trade.pair, trade.pair + 'USD'];
        for (const k of keys) {
            const v = prices[k];
            if (!v) continue;
            const p = typeof v === 'object' ? v.price : v;
            if (p && isFinite(p) && p > 0) return p;
        }
        return null;
    };

    // Push live prices into simulation trades every second via a ref-safe interval
    useEffect(() => {
        setAutoUpdateStatus('Active');
        const interval = setInterval(() => {
            const activeSim = executedTradesRef.current.filter(t => t.status === 'ACTIVE' && !t.bybitPosition);
            if (!activeSim.length || !updateTradeRef.current) return;
            activeSim.forEach(trade => {
                const livePrice = (() => {
                    const symbol = trade.pair.endsWith('USDT') ? trade.pair : trade.pair + 'USDT';
                    const keys = [symbol, trade.pair, trade.pair + 'USD'];
                    for (const k of keys) {
                        const v = pricesRef.current[k];
                        if (!v) continue;
                        const p = typeof v === 'object' ? v.price : v;
                        if (p && isFinite(p) && p > 0) return p;
                    }
                    return null;
                })();
                if (!livePrice) return;
                const entry = parseFloat(trade.entryPrice || 0);
                if (!entry) return;
                const priceChange = trade.action === 'BUY'
                    ? (livePrice - entry) / entry
                    : (entry - livePrice) / entry;
                const profitPercent = priceChange * (trade.leverage || 1) * 100;
                const profitLoss = priceChange * (trade.leverage || 1) * (trade.amount || 0);
                updateTradeRef.current(trade.id, { currentPrice: livePrice, profitPercent, profitLoss });
            });
            setLastPriceUpdate(new Date().toLocaleTimeString());
        }, 1000);
        return () => clearInterval(interval);
    }, []); // refs keep this safe — no stale closure issues

    // Calculate Statistics
    const currentStats = useMemo(() => {
        const activeTrades = executedTrades.filter(t => t.status === 'ACTIVE');
        const closedTrades = executedTrades.filter(t => t.status === 'CLOSED');
        const winningTrades = closedTrades.filter(t => t.profitLoss > 0);
        
        // Calculate current P&L for all trades (including active trades with current prices)
        // CRITICAL: Use Bybit data if available, prevent Infinity
        const totalProfit = executedTrades.reduce((sum, t) => {
            if (t.status === 'CLOSED') {
                const pnl = parseFloat(t.profitLoss || 0);
                return sum + (isFinite(pnl) ? pnl : 0);
            } else {
                // For active trades, prefer Bybit data, fallback to calculation
                if (t.bybitPosition) {
                    const bybitPnl = parseFloat(t.bybitPosition.unrealisedPnl || t.bybitPosition.unrealizedPnL || t.profitLoss || 0);
                    return sum + (isFinite(bybitPnl) ? bybitPnl : 0);
                }
                if (t.profitLoss !== undefined && t.profitLoss !== null) {
                    const pnl = parseFloat(t.profitLoss);
                    return sum + (isFinite(pnl) ? pnl : 0);
                }
                // Fallback: calculate from prices
                const { profitLoss } = calculateTradePnL(t);
                const pnl = parseFloat(profitLoss || 0);
                return sum + (isFinite(pnl) ? pnl : 0);
            }
        }, 0);
        
        // Calculate daily profit from trades created today
        const today = new Date().toDateString();
        const dailyProfit = executedTrades
            .filter(t => new Date(t.timestamp).toDateString() === today)
            .reduce((sum, t) => {
                if (t.status === 'CLOSED') {
                    const pnl = parseFloat(t.profitLoss || 0);
                    return sum + (isFinite(pnl) ? pnl : 0);
                } else {
                    // For active trades, prefer Bybit data
                    if (t.bybitPosition) {
                        const bybitPnl = parseFloat(t.bybitPosition.unrealisedPnl || t.bybitPosition.unrealizedPnL || t.profitLoss || 0);
                        return sum + (isFinite(bybitPnl) ? bybitPnl : 0);
                    }
                    if (t.profitLoss !== undefined && t.profitLoss !== null) {
                        const pnl = parseFloat(t.profitLoss);
                        return sum + (isFinite(pnl) ? pnl : 0);
                    }
                    const { profitLoss } = calculateTradePnL(t);
                    const pnl = parseFloat(profitLoss || 0);
                    return sum + (isFinite(pnl) ? pnl : 0);
                }
            }, 0);
                
        const safeTotalProfit = isFinite(totalProfit) ? totalProfit : 0;
        const safeDailyProfit = isFinite(dailyProfit) ? dailyProfit : 0;
                
        return {
            totalTrades: executedTrades.length,
            activeTrades: activeTrades.length,
            winningTrades: winningTrades.length,
            totalProfit: safeTotalProfit.toFixed(2),
            dailyProfit: safeDailyProfit.toFixed(2),
            successRate: closedTrades.length > 0 ? (winningTrades.length / closedTrades.length * 100).toFixed(1) : 0
        };
    }, [executedTrades]);

    // Effects - Market data is now provided by PriceContext

    return (
        <div className="contract-trading-dashboard">
            {/* Bybit Connection Status Banner */}
            <div className={`bybit-status-banner ${bybitConnected ? 'connected' : 'disconnected'}`} style={{
                padding: '15px 20px',
                marginBottom: '20px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: bybitConnected ? '#d4edda' : '#f8d7da',
                border: `2px solid ${bybitConnected ? '#28a745' : '#dc3545'}`,
                color: bybitConnected ? '#155724' : '#721c24',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <span style={{ fontSize: '24px' }}>{bybitConnected ? '🟢' : '🔴'}</span>
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                            Bybit Status: {bybitConnected ? 'Connected' : 'Not Connected'}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.8 }}>
                            {bybitConnectionStatus}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '12px', opacity: 0.7 }}>Available Balance</div>
                        <div style={{ fontWeight: 'bold', fontSize: '20px' }}>
                            {bybitConnected ? `$${balance.toFixed(2)}` : '$0.00'}
                        </div>
                    </div>
                </div>
            </div>


            {/* Main Content - 3 Column Layout */}
            <div className="main-content-three-column">
                {/* Left Column - Smart Signal Panel */}
                <div className="left-column-alerts">
                    <SignalPanel
                        prices={prices}
                        onApplyTrade={({ pair, action, signal }) => {
                            setManualTrade(prev => ({ ...prev, pair, action, _appliedSignal: signal }));
                            setPairSearch('');
                        }}
                    />
                </div>

                {/* Middle Column - Trading Controls */}
                <div className="middle-column-trading">
                    {/* Statistics Section */}
                    <div className="statistics-section">
                        <h3>Trading Statistics</h3>
                        
                        {/* Balance Box */}
                        <div className="balance-box">
                            <div className="balance-header">
                                <span className="balance-label">
                                    {isSimulation ? '📊 Simulation Balance' : 'Available Trading Balance'}
                                </span>
                                <span className={`balance-status ${bybitConnected ? 'connected' : 'disconnected'}`}>
                                    {bybitConnected ? '🟢 Bybit Connected' : '🔴 Not Connected'}
                                </span>
                            </div>
                            <div className="balance-amount">
                                {isSimulation
                                    ? <span style={{color:'#ffa726'}}>SIMULATION MODE</span>
                                    : bybitConnected ? `$${balance.toFixed(2)}` : '$0.00'
                                }
                            </div>
                            <div className="balance-details">
                                <div className="balance-item">
                                    <span>Bybit Status:</span>
                                    <span className={bybitConnected ? 'text-success' : 'text-error'}>
                                        {bybitConnectionStatus}
                                    </span>
                                </div>
                                {bybitConnected && !isSimulation && (
                                    <>
                                        <div className="balance-item">
                                            <span>Total Equity:</span>
                                            <span>${wsBalance?.totalEquity != null ? parseFloat(wsBalance.totalEquity).toFixed(2) : balance.toFixed(2)}</span>
                                        </div>
                                        <div className="balance-item">
                                            <span>Unrealised PnL:</span>
                                            <span className={parseFloat(wsBalance?.unrealisedPnl || 0) >= 0 ? 'text-success' : 'text-error'}>
                                                {wsBalance?.unrealisedPnl != null
                                                    ? `${parseFloat(wsBalance.unrealisedPnl) >= 0 ? '+' : ''}$${parseFloat(wsBalance.unrealisedPnl).toFixed(2)}`
                                                    : '$0.00'
                                                }
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                            {/* Simulation toggle */}
                            <div className="sim-toggle-row">
                                <label className="sim-toggle-label">
                                    <span>{isSimulation ? '🟡 Practice Mode (no real trades)' : '🔴 Live Mode (real money)'}</span>
                                    <div
                                        className={`sim-toggle ${isSimulation ? 'sim-on' : 'sim-off'}`}
                                        onClick={() => {
                                            if (!isSimulation) {
                                                if (!window.confirm('Switch to LIVE mode? Real money will be used for all trades!')) return;
                                            }
                                            setIsSimulation(prev => !prev);
                                        }}
                                    >
                                        <div className="sim-toggle-thumb" />
                                    </div>
                                </label>
                            </div>
                        </div>

            <div className="stats-grid">
                <div className="stat-card">
                                <div className="stat-label">Total Trades</div>
                                <div className="stat-value">{currentStats.totalTrades}</div>
                </div>
                <div className="stat-card">
                                <div className="stat-label">Winning Trades</div>
                                <div className="stat-value">{currentStats.winningTrades}</div>
                </div>
                                <div className="stat-card">
                                <div className="stat-label">Total Profit</div>
                                <div className={`stat-value ${parseFloat(currentStats.totalProfit) >= 0 ? 'positive' : 'negative'}`}>
                                    ${(() => {
                                        const profit = parseFloat(currentStats.totalProfit) || 0;
                                        return isFinite(profit) ? profit.toFixed(2) : '0.00';
                                    })()}
                                </div>
                </div>
                <div className="stat-card">
                                <div className="stat-label">Daily Profit</div>
                                <div className={`stat-value ${parseFloat(currentStats.dailyProfit) >= 0 ? 'positive' : 'negative'}`}>
                                    ${(() => {
                                        const profit = parseFloat(currentStats.dailyProfit) || 0;
                                        return isFinite(profit) ? profit.toFixed(2) : '0.00';
                                    })()}
                                </div>
                </div>
                <div className="stat-card">
                                <div className="stat-label">Success Rate</div>
                                <div className="stat-value">{currentStats.successRate}%</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">Active Trades</div>
                                <div className="stat-value">{currentStats.activeTrades}</div>
                    </div>
                </div>
            </div>

            {/* Manual Trading Section */}
            <div className="manual-trading-section">
                        <h3>Manual Trading</h3>
                        <div className="manual-trade-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Trading Pair:</label>
                            <input
                                type="text"
                                placeholder="Search pair (e.g. BTC)"
                                value={pairSearch}
                                onChange={e => {
                                    setPairSearch(e.target.value);
                                    const upper = e.target.value.toUpperCase();
                                    const match = availablePairs.find(p => p === upper + 'USDT' || p === upper);
                                    if (match) setManualTrade(prev => ({ ...prev, pair: match }));
                                }}
                                style={{ marginBottom: '4px', width: '100%' }}
                            />
                            <select
                                value={manualTrade.pair}
                                onChange={(e) => {
                                    setManualTrade(prev => ({ ...prev, pair: e.target.value }));
                                    setPairSearch('');
                                }}
                                size={availablePairs.length > 0 ? 1 : 1}
                            >
                                {(availablePairs.length > 0 ? availablePairs : tradingPairs.map(p => p + 'USDT'))
                                    .filter(p => !pairSearch || p.toLowerCase().includes(pairSearch.toLowerCase()))
                                    .map(pair => (
                                        <option key={pair} value={pair}>{pair}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Amount ($):</label>
                            <input 
                                type="number" 
                                value={manualTrade.amount} 
                                        onChange={(e) => setManualTrade(prev => ({ ...prev, amount: parseFloat(e.target.value) }))}
                                        min="10"
                                        max="10000"
                            />
                        </div>
                            </div>
                            <div className="form-row">
                        <div className="form-group">
                            <label>Leverage:</label>
                            <select 
                                value={manualTrade.leverage} 
                                        onChange={(e) => setManualTrade(prev => ({ ...prev, leverage: parseInt(e.target.value) }))}
                            >
                                        {[
                                            // 1x to 10x (increments of 1)
                                            ...Array.from({length: 10}, (_, i) => i + 1),
                                            // 20x to 100x (increments of 10)
                                            ...Array.from({length: 9}, (_, i) => (i + 2) * 10)
                                        ].map(lev => (
                                            <option key={lev} value={lev}>{lev}x</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                                    <label>Action:</label>
                        <div className="action-buttons">
                            <button 
                                            className={`action-btn buy ${manualTrade.action === 'BUY' ? 'active' : ''}`}
                                            onClick={() => setManualTrade(prev => ({ ...prev, action: 'BUY' }))}
                            >
                                            BUY
                            </button>
                            <button 
                                            className={`action-btn sell ${manualTrade.action === 'SELL' ? 'active' : ''}`}
                                            onClick={() => setManualTrade(prev => ({ ...prev, action: 'SELL' }))}
                            >
                                            SELL
                            </button>
                        </div>
                    </div>
                        </div>
                            <button className="execute-trade-btn" onClick={executeManualTrade} disabled={isPlacingOrder}>
                                {isPlacingOrder ? 'Placing Order...' : 'Execute Manual Trade'}
                                    </button>
                </div>
            </div>

            {/* Automated Trading Section */}
            <div className="automated-trading-section">
                        <h3>Automated Trading</h3>
                        <div className="automated-config">
                            <div className="config-row">
                                <label>
                            <input 
                                        type="checkbox" 
                                        checked={automatedConfig.enabled}
                                        onChange={(e) => setAutomatedConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                                    />
                                    Enable Automated Trading
                                </label>
                        </div>
                            <div className="config-row">
                                <div className="form-group">
                                    <label>Risk Level:</label>
                            <select 
                                        value={automatedConfig.riskLevel}
                                        onChange={(e) => setAutomatedConfig(prev => ({ ...prev, riskLevel: e.target.value }))}
                            >
                                        {riskLevels.map(level => (
                                            <option key={level.value} value={level.value}>{level.name}</option>
                                ))}
                            </select>
                        </div>
                                <div className="form-group">
                            <label>Max Trades:</label>
                            <input 
                                type="number" 
                                        value={automatedConfig.maxTrades}
                                        onChange={(e) => setAutomatedConfig(prev => ({ ...prev, maxTrades: parseInt(e.target.value) }))}
                                min="1"
                                max="20"
                            />
                        </div>
                            </div>
                            <div className="config-row">
                                <div className="form-group">
                            <label>Take Profit (%):</label>
                            <input 
                                type="number" 
                                        value={automatedConfig.takeProfit}
                                        onChange={(e) => setAutomatedConfig(prev => ({ ...prev, takeProfit: parseFloat(e.target.value) }))}
                                        min="0.1"
                                        max="50"
                                        step="0.1"
                                    />
                        </div>
                                <div className="form-group">
                                    <label>Stop Loss (%):</label>
                            <input 
                                type="number" 
                                        value={automatedConfig.stopLoss}
                                        onChange={(e) => setAutomatedConfig(prev => ({ ...prev, stopLoss: parseFloat(e.target.value) }))}
                                        min="0.1"
                                        max="20"
                                        step="0.1"
                            />
                        </div>
                        </div>
                            <div className="automated-status">
                                <span className={`status-indicator ${automatedConfig.enabled ? 'enabled' : 'disabled'}`}>
                                    {automatedConfig.enabled ? '🤖 Automated Trading ON' : '⏸️ Automated Trading OFF'}
                                </span>
                                <span className="status-note">
                                    {automatedConfig.enabled 
                                        ? 'Only profitable trades will be closed automatically' 
                                        : 'Automated trading is disabled'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Right Column - Active Trades Control */}
                <div className="right-column-trades">
                    <div className="active-trades-control">
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px'}}>
                            <h3 style={{margin:0}}>Active Trades ({executedTrades.filter(t => t.status === 'ACTIVE').length})</h3>
                            <button
                                title="Import any open Bybit positions that are missing from the dashboard"
                                style={{background:'#2d3748',border:'1px solid #4a5568',borderRadius:'6px',color:'#e2e8f0',cursor:'pointer',fontSize:'12px',padding:'5px 10px'}}
                                onClick={async () => {
                                    try {
                                        const r = await fetch('/api/bybit/sync-positions', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({}) });
                                        const d = await r.json();
                                        if (d.success) {
                                            const msg = `✅ Sync complete!\n• ${d.synced} position(s) added\n• ${d.ghostsClosed || 0} ghost trade(s) removed\n• ${d.dupsRemoved || 0} duplicate(s) removed\n• ${d.total} real position(s) on Bybit\n\nPage will reload to reflect changes.`;
                                            alert(msg);
                                            window.location.reload();
                                        } else {
                                            alert('❌ Sync failed: ' + d.error);
                                        }
                                    } catch(e) { alert('❌ Sync error: ' + e.message); }
                                }}
                            >🔄 Sync Bybit</button>
                        </div>
                        <div className="price-update-indicator">
                            <span className="update-label">Last Price Update:</span>
                            <span className="update-time">{lastPriceUpdate}</span>
                        </div>
                        <div className="trades-list">
                            {executedTrades.filter(t => t.status === 'ACTIVE').length === 0 && (
                                <div className="no-trades-msg">No active trades. Open a trade using the form below.<br/><small style={{color:'#718096'}}>If you have open Bybit positions not showing here, click "🔄 Sync Bybit" above.</small></div>
                            )}
                            {executedTrades.filter(t => t.status === 'ACTIVE').map(trade => {
                                const { profitPercent, profitLoss } = calculateTradePnL(trade);
                                const safePnlPct = isFinite(profitPercent) ? profitPercent : 0;
                                const safePnlUsd = isFinite(profitLoss) ? profitLoss : 0;
                                const isProfit = safePnlUsd >= 0;

                                // Entry / current price — always show latest live data
                                const entryPrice = parseFloat(trade.bybitPosition?.avgPrice || trade.bybitPosition?.entryPrice || trade.entryPrice || 0);
                                const currentPrice = parseFloat(
                                    trade.bybitPosition?.markPrice   // real trade: Bybit mark price
                                    || getLivePrice(trade)           // sim trade: live price from context
                                    || trade.currentPrice            // fallback: last stored price
                                    || entryPrice
                                    || 0
                                );
                                const fmt = (p) => p > 0 ? `$${p.toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:6})}` : 'Syncing…';
                                const entryDisplay = fmt(entryPrice);
                                const currentDisplay = fmt(currentPrice);

                                // How long the trade has been open
                                const openedMs = Date.now() - new Date(trade.timestamp).getTime();
                                const openedMins = Math.floor(openedMs / 60000);
                                const durationStr = openedMins < 60
                                    ? `${openedMins}m`
                                    : `${Math.floor(openedMins/60)}h ${openedMins%60}m`;

                                // Conservative SL/TP levels (3% / 6% of entry, adjusted for leverage)
                                const slPrice = entryPrice > 0
                                    ? (trade.action === 'BUY' ? entryPrice * (1 - 0.03/(trade.leverage||1)) : entryPrice * (1 + 0.03/(trade.leverage||1)))
                                    : 0;
                                const tpPrice = entryPrice > 0
                                    ? (trade.action === 'BUY' ? entryPrice * (1 + 0.06/(trade.leverage||1)) : entryPrice * (1 - 0.06/(trade.leverage||1)))
                                    : 0;

                                // Progress bar: how far from SL to TP (clamped 0–100)
                                let progress = 50;
                                if (slPrice > 0 && tpPrice > 0 && currentPrice > 0) {
                                    const range = Math.abs(tpPrice - slPrice);
                                    const dist = trade.action === 'BUY' ? currentPrice - slPrice : slPrice - currentPrice;
                                    progress = Math.max(0, Math.min(100, (dist / range) * 100));
                                }

                                return (
                                    <div key={trade.id} className={`trade-item trade-item-rich ${isProfit ? 'trade-profit' : 'trade-loss'}`}>
                                        {/* Header row */}
                                        <div className="trade-header">
                                            <div className="trade-pair-info">
                                                <span className={`trade-direction-badge ${trade.action === 'BUY' ? 'long' : 'short'}`}>
                                                    {trade.action === 'BUY' ? '▲ LONG' : '▼ SHORT'}
                                                </span>
                                                <span className="trade-pair-name">{trade.pair}</span>
                                                {trade.isSimulation && <span className="sim-badge">SIM</span>}
                                                <span className="trade-leverage-badge">{trade.leverage}x</span>
                                            </div>
                                            <button className="close-trade-btn" onClick={() => handleCloseTrade(trade.id)} title="Close Trade">✕</button>
                                        </div>

                                        {/* P&L row — the most important thing */}
                                        <div className={`trade-pnl-large ${isProfit ? 'positive' : 'negative'}`}>
                                            <span className="pnl-usd">{isProfit ? '+' : ''}${safePnlUsd.toFixed(2)}</span>
                                            <span className="pnl-pct">({isProfit ? '+' : ''}{safePnlPct.toFixed(2)}%)</span>
                                        </div>

                                        {/* Price row */}
                                        <div className="trade-price-row">
                                            <div className="price-col">
                                                <span className="price-lbl">Entry</span>
                                                <span className="price-val">{entryDisplay}</span>
                                            </div>
                                            <div className="price-arrow">{trade.action === 'BUY' ? (currentPrice >= entryPrice ? '↑' : '↓') : (currentPrice <= entryPrice ? '↑' : '↓')}</div>
                                            <div className="price-col">
                                                <span className="price-lbl">Current</span>
                                                <span className={`price-val ${isProfit ? 'text-success' : 'text-error'}`}>{currentDisplay}</span>
                                            </div>
                                        </div>

                                        {/* SL / TP levels */}
                                        {slPrice > 0 && (
                                            <div className="trade-sl-tp-row">
                                                <span className="sl-label">🛑 SL: ${slPrice.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:4})}</span>
                                                <span className="tp-label">🎯 TP: ${tpPrice.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:4})}</span>
                                            </div>
                                        )}

                                        {/* Progress bar SL → TP */}
                                        {slPrice > 0 && (
                                            <div className="trade-progress-bar">
                                                <div className="progress-track">
                                                    <div className="progress-fill" style={{width:`${progress}%`, background: progress > 50 ? '#4caf50' : progress > 25 ? '#ffa726' : '#f44336'}} />
                                                </div>
                                                <div className="progress-labels">
                                                    <span>SL</span><span>TP</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Footer: amount, duration, close button */}
                                        <div className="trade-footer-row">
                                            <span className="trade-meta">${trade.amount} · {durationStr} open</span>
                                            <button className="close-trade-btn-small" onClick={() => handleCloseTrade(trade.id)}>
                                                Close Trade
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractTrading;